import type { Tracker, PageViewProps } from './types';
import { GtmTracker } from './trackers/gtm';
import { ConsoleTracker } from './trackers/console-tracker';
import { PostHogTracker } from './trackers/posthog';
import type { GdprSettings } from '@/providers/Cookie/types';

/**
 * Tracking service that manages multiple trackers
 */
export class TrackingService {
  private trackers: Tracker[] = [];
  private isInitialized = false;

  constructor() {
    const gtmContainerId = import.meta.env.VITE_GTM_CONTAINER_ID;
    if (gtmContainerId) {
      this.addTracker(new GtmTracker(gtmContainerId));
    }

    const posthogToken = import.meta.env.VITE_POSTHOG_PROJECT_TOKEN;
    if (posthogToken) {
      this.addTracker(new PostHogTracker());
    }

    const isDevelopment = import.meta.env.MODE === 'development';
    const isConsoleTrackingEnabled =
      import.meta.env.VITE_CONSOLE_TRACKING_ENABLED === 'true';
    if (isDevelopment && isConsoleTrackingEnabled) {
      this.addTracker(new ConsoleTracker());
    }
  }

  /**
   * Add a tracker to the service
   */
  addTracker(tracker: Tracker): void {
    this.trackers.push(tracker);
    if (this.isInitialized) {
      tracker.init();
    }
  }

  /**
   * Remove a tracker from the service
   */
  removeTracker(tracker: Tracker): void {
    const index = this.trackers.indexOf(tracker);
    if (index > -1) {
      this.trackers.splice(index, 1);
    }
  }

  /**
   * Initialize all trackers
   */
  async init(): Promise<void> {
    if (this.isInitialized) {
      return;
    }

    const initPromises = this.trackers.map((tracker) => tracker.init());
    await Promise.all(initPromises);
    this.isInitialized = true;
  }

  /**
   * Track a page view across all trackers
   */
  async trackPageView(props?: PageViewProps): Promise<void> {
    const defaultProps: PageViewProps = {
      page_path: window.location.pathname,
      page_title: document.title,
      page_location: window.location.href,
      ...props,
    };

    const trackPromises = this.trackers.map((tracker) =>
      tracker.trackPageView(defaultProps)
    );
    await Promise.all(trackPromises);
  }

  /**
   * Track a custom event across all trackers
   */
  async trackEvent(
    eventName: string,
    props?: Record<string, unknown>
  ): Promise<void> {
    const trackPromises = this.trackers
      .filter((tracker) => tracker.trackEvent)
      .map((tracker) => tracker.trackEvent!(eventName, props));
    await Promise.all(trackPromises);
  }

  /**
   * Associate the current session with a known user across all trackers
   * @param userId - Stable, non-PII user identifier
   * @param props - Optional properties to attach to the user
   */
  async identify(
    userId: string,
    props?: Record<string, unknown>
  ): Promise<void> {
    const identifyPromises = this.trackers
      .filter((tracker) => tracker.identify)
      .map((tracker) => tracker.identify!(userId, props));
    await Promise.all(identifyPromises);
  }

  /**
   * Attach properties to every event sent from now on across all trackers
   * @param props - Properties to attach
   */
  async register(props: Record<string, unknown>): Promise<void> {
    const registerPromises = this.trackers
      .filter((tracker) => tracker.register)
      .map((tracker) => tracker.register!(props));
    await Promise.all(registerPromises);
  }

  /**
   * Reset the current user association across all trackers (e.g. on logout)
   */
  async reset(): Promise<void> {
    const resetPromises = this.trackers
      .filter((tracker) => tracker.reset)
      .map((tracker) => tracker.reset!());
    await Promise.all(resetPromises);
  }

  /**
   * Get all registered trackers
   */
  getTrackers(): Tracker[] {
    return [...this.trackers];
  }

  /**
   * Sync consent preferences for all trackers
   * @param consents - Cookie consents from the cookie provider
   */
  syncConsents(
    preferences: {
      analytics: boolean;
      necessary: boolean;
      marketing: boolean;
      functional: boolean;
    } | null
  ): void {
    const consents: GdprSettings['consents'] = preferences
      ? {
          analytics: preferences.analytics,
          essential: preferences.necessary,
          marketing: preferences.marketing,
          personalization: preferences.functional,
          uncategorized: false,
        }
      : {
          analytics: false,
          essential: true,
          marketing: false,
          personalization: false,
          uncategorized: false,
        };

    // Update all trackers that implement updateConsent
    this.trackers.forEach((tracker) => {
      if (tracker.updateConsent) {
        tracker.updateConsent(consents);
      }
    });
  }
}

export const trackingService = new TrackingService();
